/**
 * 노미네이터 정보(L_NOMINATOR.NM_JSON_DATA — bizfile 원본 필드명) 표시 포맷
 *   엔티티 Nominee 뱃지 모달 · 챗봇 Nominator 상세 공용
 */
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';

export type NominatorData = Record<string, any>;

export interface NominatorFieldOption {
  key: string;
  label: string;
  value: string;
  full?: boolean;
}

export const nominatorText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

/** NominatorCategory '1' = Individual, 그 외 = Corporate */
export const isIndividualNominator = (nominator?: NominatorData | null): boolean =>
  nominatorText(nominator?.NominatorCategory) === '1';

export const formatNominatorDate = (value: unknown): string => {
  const text = nominatorText(value);
  if (!text) return '';
  return formatDateByJurisdiction(text.slice(0, 10)) || text;
};

export const formatNominatorPhone = (nominator?: NominatorData | null): string => {
  if (!nominator) return '';
  const countryCode = nominatorText(nominator.CountryCode);
  return [countryCode ? `+${countryCode.replace(/^\+/, '')}` : '', nominatorText(nominator.AreaCode), nominatorText(nominator.ContactNo)]
    .filter(Boolean)
    .join(' ');
};

/** 싱가포르 주소 우선, 없으면 Foreign 주소 */
export const formatNominatorAddress = (nominator?: NominatorData | null): string => {
  if (!nominator) return '';
  const n = nominator;
  const blockStreet = [nominatorText(n.BlkhseNo), nominatorText(n.StreetName)].filter(Boolean).join(' ');
  const levelUnit = nominatorText(n.LevelNo) || nominatorText(n.UnitNo)
    ? `#${[nominatorText(n.LevelNo), nominatorText(n.UnitNo)].filter(Boolean).join('-')}`
    : '';
  const postal = nominatorText(n.PostalCode) ? `Singapore ${nominatorText(n.PostalCode)}` : '';
  const local = [blockStreet, [levelUnit, nominatorText(n.BuildingName)].filter(Boolean).join(' '), postal]
    .filter(Boolean)
    .join(', ');
  if (local) return local;
  return [nominatorText(n.ForeignAddr1), nominatorText(n.ForeignAddr2)].filter(Boolean).join(', ');
};

/** 노미네이터 변수인지 (nominator_full_name, nominator_entity_uen, date_of_nomination …) */
export const isNominatorVariable = (text: string): boolean =>
  /nominat(or|ors|ing)|nominatedby|dateofnomination/i.test(String(text || '').replace(/[^a-z0-9]/gi, ''));

// 변수명(공백·기호 제거, 'nominator' 제거) 에 포함된 표현 → buildNominatorFieldOptions 항목 key (위에서부터 우선)
const NOMINATOR_VARIABLE_ALIASES: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /dateofnomination|nominationdate/, key: 'nominationDate' },
  { pattern: /dateofbirth|birthdate|dob/, key: 'dateOfBirth' },
  { pattern: /registeredofficeaddress|registeredoffice|residentialaddress|address/, key: 'address' },
  { pattern: /identificationtype|idtype/, key: 'identificationType' },
  { pattern: /corporateregistername|corporateentityregister|entityregistername|registername/, key: 'corporateEntityRegister' },
  { pattern: /corporateregisterno|registrationno|regno|uen/, key: 'uen' },
  { pattern: /identificationno|identificationnumber|idno|nric|passport/, key: 'identificationNo' },
  { pattern: /nationality/, key: 'nationality' },
  { pattern: /alias/, key: 'nameAlias' },
  { pattern: /legalform/, key: 'legalForm' },
  { pattern: /jurisdiction|countryofregistration|placeofincorporation|countryofincorporation/, key: 'countryOfRegistration' },
  { pattern: /statute/, key: 'statute' },
  { pattern: /email/, key: 'email' },
  { pattern: /contactno|contactnumber|phone|mobile|telephone/, key: 'contactNo' },
  { pattern: /entityname|fullname|name/, key: 'name' },
];

// 법인 / 개인 노미네이터 전용 표현 — 공통 항목(name·email·contactNo·address·nominationDate)의 대상 구분
const CORPORATE_NOMINATOR_HINT = /entity|corporate|registeredoffice|legalform|jurisdiction|statute|uen/;
const INDIVIDUAL_NOMINATOR_HINT = /fullname|alias|residential|nationality|identification|birth|nric|passport/;

/**
 * 노미네이터 변수명 → 항목 key 와 대상 구분
 * @returns key: buildNominatorFieldOptions 의 key / category: 'corporate' | 'individual' | null(둘 다)
 */
export const resolveNominatorVariable = (
  text: string,
): { key: string; category: 'corporate' | 'individual' | null } | null => {
  const target = String(text || '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase()
    .replace(/_?\d+$/, '')
    .replace(/nominators?/g, '');
  const matched = NOMINATOR_VARIABLE_ALIASES.find((alias) => alias.pattern.test(target));
  if (!matched) return null;
  const category = CORPORATE_NOMINATOR_HINT.test(target)
    ? 'corporate'
    : (INDIVIDUAL_NOMINATOR_HINT.test(target) ? 'individual' : null);
  return { key: matched.key, category };
};

/**
 * 노미네이터 한 건에서 변수에 들어갈 값 (대상 구분이 다르거나 값이 없으면 '')
 */
export const getNominatorVariableValue = (
  text: string,
  nominator: NominatorData | null | undefined,
  display: Record<string, string> = {},
): string => {
  if (!nominator) return '';
  const resolved = resolveNominatorVariable(text);
  if (!resolved) return '';
  const isIndividual = isIndividualNominator(nominator);
  if (resolved.category === 'corporate' && isIndividual) return '';
  if (resolved.category === 'individual' && !isIndividual) return '';
  const option = buildNominatorFieldOptions(nominator, display).find((opt) => opt.key === resolved.key);
  return option?.value || '';
};

/**
 * 카테고리별 표시 항목 (Individual / Corporate)
 * @param display 코드 → L_BASECODE 표시명 (Nationality / CountryOfRegistration / IdentificationType)
 */
export const buildNominatorFieldOptions = (
  nominator: NominatorData | null | undefined,
  display: Record<string, string> = {},
): NominatorFieldOption[] => {
  if (!nominator) return [];
  const n = nominator;
  const displayOf = (field: string) => nominatorText(display[field]) || nominatorText(n[field]);

  const common: NominatorFieldOption[] = [
    { key: 'email', label: 'Email', value: nominatorText(n.EmailAddress) },
    { key: 'contactNo', label: 'Contact No', value: formatNominatorPhone(n) },
    { key: 'address', label: isIndividualNominator(n) ? 'Residential Address' : 'Registered Office Address', value: formatNominatorAddress(n), full: true },
    { key: 'nominationDate', label: 'Date of Nomination', value: formatNominatorDate(n.NominationDate) },
  ];

  if (isIndividualNominator(n)) {
    return [
      { key: 'name', label: 'Full Name', value: nominatorText(n.Name) },
      { key: 'nameAlias', label: 'Alias', value: nominatorText(n.NameAlias) },
      { key: 'identificationType', label: 'Identification Type', value: displayOf('IdentificationType') },
      { key: 'identificationNo', label: 'Identification No', value: nominatorText(n.IdentificationNo) },
      { key: 'nationality', label: 'Nationality', value: displayOf('Nationality') },
      { key: 'dateOfBirth', label: 'Date of Birth', value: formatNominatorDate(n.BirthDate) },
      ...common,
    ];
  }

  return [
    { key: 'name', label: 'Entity Name', value: nominatorText(n.EntityName) },
    { key: 'uen', label: 'UEN', value: nominatorText(n.Uen) },
    { key: 'legalForm', label: 'Legal Form', value: nominatorText(n.CorporateControllerLegalForm) },
    { key: 'countryOfRegistration', label: 'Country of Registration', value: displayOf('CountryOfRegistration') },
    { key: 'statute', label: 'Statute', value: nominatorText(n.Statute) },
    { key: 'corporateEntityRegister', label: 'Corporate Entity Register', value: nominatorText(n.CorporateEntityRegisterName) },
    ...common,
  ];
};
